import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useI18n } from "../lib/i18n";

export function ServiceAboutSection({ description, tags }: { description: string; tags: string[] }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [hasMoreThanThreeLines, setHasMoreThanThreeLines] = useState(false);
  const measureRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const paragraph = measureRef.current;
    if (!paragraph) return;
    const measure = () => {
      const lineHeight = Number.parseFloat(getComputedStyle(paragraph).lineHeight);
      setHasMoreThanThreeLines(paragraph.scrollHeight > lineHeight * 3 + 1);
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(paragraph);
    return () => observer.disconnect();
  }, [description]);

  return (
    <section className="card order-3 rounded-none border-x-0 border-t-8 border-t-paper shadow-none p-5 lg:order-none lg:rounded-xl2 lg:border lg:border-t-line lg:shadow-card">
      <h2 className="font-bold text-navy text-lg mb-2">{t("detail.service.about")}</h2>
      <div className="relative">
        <p className={`text-sm text-mut whitespace-pre-line ${!expanded ? "line-clamp-3 lg:line-clamp-4" : ""}`}>{description}</p>
        <p ref={measureRef} aria-hidden="true" className="absolute inset-x-0 top-0 invisible pointer-events-none text-sm whitespace-pre-line">{description}</p>
      </div>
      {!!tags.length && <div className="service-detail-tags">{tags.map(tag => <span key={tag}>{tag}</span>)}</div>}
      {hasMoreThanThreeLines && (
        <button type="button" onClick={() => setExpanded((open) => !open)} aria-expanded={expanded}
          className="mt-2 block ml-auto text-sm font-semibold text-blue-700 hover:text-blue-900 lg:hidden">
          {expanded ? t("common.showLess", "Show less") : t("common.viewMore", "View more")}
        </button>
      )}
      {description.length > 240 && (
        <button type="button" onClick={() => setExpanded((open) => !open)} aria-expanded={expanded}
          className="hidden lg:inline-flex mt-2 text-sm font-semibold text-blue-700 hover:text-blue-900 items-center gap-1">
          {expanded ? <>{t("common.showLess", "Show less")} <ChevronUp className="w-4 h-4" /></> : <>{t("common.showMore", "Show more")} <ChevronDown className="w-4 h-4" /></>}
        </button>
      )}
    </section>
  );
}
